/**
 * Server-fetched content signals.
 *
 * `letters` is the static 28-letter corpus — loaded once per session.
 * `mapState` is per-user and must refetch every time the Map screen
 * mounts, otherwise the child sees stale stars after completing a stage.
 *
 * Call sites:
 *   - loadContent()    — initial boot hydration (both signals).
 *   - refreshMap()     — after any progress write or when re-entering Map.
 */

import { signal } from '@preact/signals';
import { api } from '../lib/api';
import type { MapState } from '@huroof/shared';

export interface LetterSummary {
  id: number;
  letter: string;
  name_ar: string;
  name_en: string;
  sound_url: string;
  form_isolated: string;
  form_beginning: string;
  form_middle: string;
  form_end: string;
  island_group: number;
  sort_order: number;
  stroke_path_data: unknown;
}

export const letters = signal<LetterSummary[] | null>(null);
export const mapState = signal<MapState | null>(null);
export const contentError = signal<string | null>(null);

let lettersLoader: Promise<void> | null = null;
let mapLoader: Promise<void> | null = null;

async function loadLetters(): Promise<void> {
  if (letters.value) return;
  if (lettersLoader) return lettersLoader;
  lettersLoader = (async () => {
    try {
      const resp = await api<{ ok: boolean; letters: LetterSummary[] }>(
        '/api/letters',
      );
      letters.value = resp.letters;
      contentError.value = null;
    } catch (err) {
      contentError.value = err instanceof Error ? err.message : String(err);
    } finally {
      lettersLoader = null;
    }
  })();
  return lettersLoader;
}

/** Force-refetch map state. Call after any progress write. */
export function refreshMap(): Promise<void> {
  if (mapLoader) return mapLoader;
  mapLoader = (async () => {
    try {
      const resp = await api<{ ok: boolean; map: MapState }>('/api/map');
      mapState.value = resp.map;
      contentError.value = null;
    } catch (err) {
      contentError.value = err instanceof Error ? err.message : String(err);
    } finally {
      mapLoader = null;
    }
  })();
  return mapLoader;
}

export async function loadContent(): Promise<void> {
  await Promise.all([loadLetters(), refreshMap()]);
}
